import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import { Clock, MapPin, X, Play, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

function getPosition(need) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ latitude: null, longitude: null });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve({ latitude: null, longitude: null }),
      { enableHighAccuracy: true, timeout: 9000, maximumAge: 0 }
    );
  });
}

function elapsedStr(fromIso) {
  const diff = Math.max(0, Date.now() - new Date(fromIso).getTime());
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function ClockWidget() {
  const { t } = useT();
  const { activeId } = useCenter();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(() => {
    if (!activeId) return;
    api.get("/timeclock/me").then((r) => setState(r.data)).catch(() => setState(null));
  }, [activeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const i = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(i); }, []);

  if (!state) return null;
  const clockedIn = state.clocked_in;

  const punch = async (kind) => {
    setBusy(true);
    try {
      const pos = state.geo_enabled ? await getPosition(true) : { latitude: null, longitude: null };
      const { data } = await api.post(`/timeclock/${kind}`, pos);
      if (kind === "clock-in") { toast.success(t("clockIn") + " ✓"); setState((s) => ({ ...s, clocked_in: true, entry: data })); }
      else { toast.success(t("clockOut") + " ✓"); setState((s) => ({ ...s, clocked_in: false, entry: null })); }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-80 rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-fade-up" data-testid="clock-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-white">
            <span className="font-display font-bold flex items-center gap-2"><Clock className="w-4 h-4" />{t("timeClock")}</span>
            <button onClick={() => setOpen(false)} data-testid="clock-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5 text-center space-y-4">
            {clockedIn ? (
              <>
                <p className="text-xs text-muted-foreground">{t("clockedInSince")} {state.entry?.clock_in?.slice(11, 16)}</p>
                <p className="font-mono text-3xl font-bold tabular-nums" data-testid="clock-elapsed">{elapsedStr(state.entry.clock_in)}</p>
                <button disabled={busy} onClick={() => punch("clock-out")} data-testid="clock-out-btn"
                  className="w-full py-3 rounded-xl bg-destructive text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Square className="w-5 h-5" />}{t("clockOut")}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">{t("notClockedIn")}</p>
                <button disabled={busy} onClick={() => punch("clock-in")} data-testid="clock-in-btn"
                  className="w-full py-3 rounded-xl bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}{t("clockIn")}
                </button>
              </>
            )}
            {state.geo_enabled && (
              <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1"><MapPin className="w-3 h-3" />{t("geoRequiredNote")}</p>
            )}
          </div>
        </div>
      )}

      {/* Floating button */}
      <button onClick={() => setOpen((o) => !o)} data-testid="clock-fab"
        className={`fixed bottom-4 right-4 sm:right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-transform hover:scale-105 ${clockedIn ? "bg-emerald-600 ring-4 ring-emerald-500/30 animate-pulse" : "bg-primary"}`}
        title={t("timeClock")}>
        <Clock className="w-6 h-6" />
      </button>
    </>
  );
}
